import {
  ReturnToCheckoutModal,
  StageName,
} from '@cinch-labs/shared-ui-return-to-checkout-modal'
import { SharedUiHeaderV2 } from '@cinch-labs/shared-ui-header-v2'
import { checkoutTimes, getLastCheckoutStage } from '@cinch-labs/shared-util'
import { useFavouriteVehicles } from '@cinch-nx/data-favourites'
import { useOrdersStore } from '@cinch-nx/data-orders'
import type { VapsHero } from '@cinch-nx/data-storyblok'
import { useUserStore } from '@cinch-nx/data-user'
import {
  useAnalytics,
  TrackingEvent,
  TrackingEventTypes,
} from '@cinch-nx/shared-analytics'
import { Gradient } from '@cinch-nx/shared-ui'
import { SharedUiFooter } from '@cinch-nx/shared-ui-footer'
import cx from 'classnames'
import { AppProps as NextAppProps } from 'next/app'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Breadcrumbs from '../breadcrumbs/breadcrumbs'
import type { Breadcrumbs as TBreadcrumbs } from '../breadcrumbs/types'
import { Hero } from '../hero/hero'
import styles from './layout.module.css'
import { Env, readFromEnv } from '@cinch-nx/environments'

type withLayoutProps = { layoutProps: LayoutProps }
// eslint-disable-next-line @typescript-eslint/ban-types
type PageComponentProps = {}

export interface AppPropsWithLayoutProps<PageProps = PageComponentProps>
  extends NextAppProps<PageProps> {
  pageProps: PageProps & withLayoutProps
}

export interface AppPropsWithLayoutPropsWithError<
  PageProps = PageComponentProps,
> extends AppPropsWithLayoutProps<PageProps & { err?: Error }> {
  err?: Error
  pageProps: PageProps & withLayoutProps
}

export interface LayoutProps {
  children: React.ReactElement
  breadCrumbs?: TBreadcrumbs[]
  heroProps?: VapsHero
  showBorderRadius?: boolean
  showFooterBorderRadius?: boolean
  useVerticalGradient?: boolean
  shouldShowSharedUIHeaderV1?: boolean
}

export function Layout({
  children,
  breadCrumbs,
  heroProps,
  showBorderRadius = false,
  showFooterBorderRadius = true,
  useVerticalGradient,
}: LayoutProps) {
  const showHeroInHeader = !!heroProps
  const trackEvent = useAnalytics((state) => state.trackEvent)
  const stageName = (process.env['STAGE_NAME'] ?? 'local') as StageName
  const trackedEvent = useRef<string | null>(null)

  const {
    refreshActiveOrder,
    checkIfVehicleIsReserved,
    extendOrderExpiryDate,
    activeOrder,
  } = useOrdersStore()

  const { isLoggedIn, firstName, email, token } = useUserStore((state) => ({
    isLoggedIn: state.status === 'valid',
    firstName: state.profile?.firstName,
    email: state.profile?.email,
    token: state.token,
  }))

  const [shouldShowCheckoutModal, setShouldShowCheckoutModal] = useState(false)
  const [shouldShowBasketIcon, setShouldShowBasketIcon] = useState(false)

  const { timeRemainingInSeconds, isExpiringSoon } = checkoutTimes(activeOrder)

  const { favouritesData } = useFavouriteVehicles()

  const favourites = useMemo(
    () => favouritesData?.favouriteVehicles ?? [],
    [favouritesData],
  )

  useEffect(() => {
    if (activeOrder?.orderRef) {
      setShouldShowBasketIcon(true)
    }
  }, [activeOrder?.orderRef])

  useEffect(() => {
    const internalAPIs = [
      'landing.api.cinch.co.uk',
      'landing-articles.snc-prod.aws.cinch.co.uk',
      'vaps-product-pages.cnv-dev.aws.cinch.co.uk',
    ]
    if (
      !window.location?.href.includes('_storyblok') &&
      !window.location?.href.endsWith('sitemap.xml') &&
      internalAPIs.some((p) => window.location?.href.includes(p)) &&
      process.env['STAGE_NAME'] === 'production'
    ) {
      window.location.href = `https://www.cinch.co.uk${
        window.location.pathname ?? ''
      }`
    }
  }, [])

  const handleShouldShowBasketIcon = useCallback(() => {
    setShouldShowBasketIcon(!shouldShowBasketIcon)
  }, [shouldShowBasketIcon])

  const handleTrackEvent = useCallback(
    (data: Record<string, unknown>) => {
      const trackedValue = `${data.label} ${data.action}`

      if (trackedValue !== trackedEvent.current) {
        trackEvent({
          type: TrackingEventTypes.ADOBE,
          eventName: data.name,
          data: {
            event: data,
          },
        } as unknown as TrackingEvent)

        trackedEvent.current = trackedValue as string
        return
      }
      if (trackedEvent.current !== null) {
        trackedEvent.current = null
      }
    },
    [trackEvent],
  )

  const gapMenuItemShown = readFromEnv(Env.GapInsuranceFlag) === 'true'
  const updatedCinchCareCopy =
    readFromEnv(Env.ServicingAndWarrantyFlag) === 'true'

  const isCinchApp =
    typeof window !== 'undefined' &&
    window.navigator?.userAgent.includes('cinchapp')

  return (
    <>
      {!isCinchApp && (
        <div
          className={cx(styles['headerContainer'], {
            [styles['verticalGradient']]: useVerticalGradient,
            [styles['borderRadiusHeader']]: showBorderRadius,
          })}
        >
          <SharedUiHeaderV2
            handleTrackEvent={handleTrackEvent}
            stageName={stageName}
            isLoggedIn={isLoggedIn}
            accessToken={token?.access_token || ''}
            setIsCheckoutModalVisible={() =>
              setShouldShowCheckoutModal(!shouldShowCheckoutModal)
            }
            isCheckoutModalVisible={shouldShowCheckoutModal}
            firstName={firstName}
            email={email}
            favourites={favourites}
            refreshActiveOrder={refreshActiveOrder}
            shouldShowBasketIcon={shouldShowBasketIcon}
            checkoutModal={
              <ReturnToCheckoutModal
                activeOrder={activeOrder || undefined}
                handleShouldShowBasketIcon={handleShouldShowBasketIcon}
                lastVisitedCheckoutStage={getLastCheckoutStage(activeOrder)}
                onClose={() =>
                  setShouldShowCheckoutModal(!shouldShowCheckoutModal)
                }
                timeRemainingInSeconds={timeRemainingInSeconds}
                withinExpiryTimeWindow={!!isExpiringSoon}
                isLoggedIn={isLoggedIn}
                accessToken={token?.access_token || ''}
                refreshActiveOrder={refreshActiveOrder}
                checkIfVehicleIsReserved={checkIfVehicleIsReserved}
                extendOrderExpiryDate={extendOrderExpiryDate}
                trackEvent={trackEvent}
              />
            }
            isGapMenuItemShown={gapMenuItemShown}
            isUpdatedCinchCareCopy={updatedCinchCareCopy}
          >
            {showHeroInHeader ? <Hero {...heroProps} /> : undefined}
          </SharedUiHeaderV2>
        </div>
      )}

      <main
        id="main-content"
        className={cx({
          [styles['borderRadiusMain']]: showBorderRadius,
        })}
      >
        {breadCrumbs && <Breadcrumbs breadcrumbs={breadCrumbs} />}
        <Gradient />
        {children}
      </main>

      {!isCinchApp && (
        <SharedUiFooter showFooterBorderRadius={showFooterBorderRadius} />
      )}
    </>
  )
}

export default Layout
